<script>
import _ from 'underscore'

import { mapGetters } from 'vuex'
import { LMap, LMarker, LIcon, LPopup, LControl, LControlZoom } from 'vue2-leaflet'
import Vue2LeafletGoogleMutant from 'vue2-leaflet-googlemutant'
import { GestureHandling } from 'leaflet-gesture-handling'
import LControlFullscreen from 'vue2-leaflet-fullscreen'

import Likable from '@src/mixins/likable.js'
import Translatable from '@src/mixins/translatable.js'
import Resizable from '@src/mixins/resizable.js'

import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css'
import 'leaflet/dist/leaflet.css'

import marker from '@assets/icons/marker.svg'

export default {
  name: 'AttractionsMap',
  components: {
    LMap,
    LMarker,
    LIcon,
    LPopup,
    Vue2LeafletGoogleMutant,
    LControlFullscreen,
    LControl,
    LControlZoom
  },
  mixins: [
    Likable,
    Translatable,
    Resizable
  ],
  props: {
    attractions: {
      type: Array,
      required: true
    },
    filters: {
      type: Array,
      required: true
    },
    searchRegion: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      API_KEY: process.env.GOOGLE_MAPS_API_KEY,
      constants: {
        ZOOM_LEVEL: {
          CITY: 10,
          STREETS: 15
        }
      },
      mapOptions: {
        zoomSnap: 0.5,
        gestureHandling: true,
        zoomControl: false
      },
      selectedFilter: 'attractions'
    }
  },
  computed: {
    ...mapGetters('attractions', [
      'freeEntryAttractions',
      'likedAttractions'
    ]),
    attractionList() {
      if (this.filters.length) {
        return this[this.selectedFilter]
        .filter(attraction => _.intersection(attraction.categories, this.filters).length)
      } else {
        return this[this.selectedFilter]
      }
    },
    attractionListRegions() {
      if (this.searchRegion === 'all') {
        return this.attractionList
      } else {
        return this.attractionList
        .filter(attraction => attraction.area === this.searchRegion)
      }
    },
    markers() {
      return this.attractionListRegions.map(attraction => {
        return {
          position: {
            latitude: parseFloat(attraction.lat),
            longitude: parseFloat(attraction.lon)
          },
          icon: this.getMarkerIcon(),
          attractionData: {
            image: `${process.env.VUE_APP_STATIC_URL}${_.sample(attraction.webimages)}`,
            content: attraction.content,
            address: attraction.address1,
            id: attraction._id,
            slug: attraction.slug
          }
        }
      }).filter(marker => marker.position.latitude && marker.position.longitude)
    }
  },
  methods: {
    calculateCenterCoords() {
      if (this.markers.length) {
        const avgLat = this.markers.reduce((sum, marker) => {
          return (sum + marker.position.latitude)
        }, 0) / this.markers.length;

        const avgLong= this.markers.reduce((sum, marker) => {
          return (sum + marker.position.longitude)
        }, 0) / this.markers.length;

        return {
          lat: avgLat,
          lng: avgLong
        }
      }

      return [50.083, 14.417]
    },
    centerMap(marker) {
      this.$refs.map.setCenter(this.getPosition(marker))
    },
    getMarkerIcon() {
      return `${marker}`
    },
    getPosition(marker) {
      return [marker.position.latitude, marker.position.longitude]
    },
    getBackground(img) {
      return {
        backgroundImage: `url(${img})`
      }
    },
    openPopup: function (event) {
      if (this.desktopScreen) event.target.openPopup()
    },
    closePopup: function (event) {
      if (this.desktopScreen) event.target.closePopup()
    },
    navigate(marker) {
      if (!this.desktopScreen) {
        this.centerMap(marker)
      } else {
        this.$router.push({
          name: 'attraction',
          params: {
            slug: marker.attractionData.slug,
          }
        })
      }
    },
    filter(type) {
      this.selectedFilter = type
    }
  },
  mounted() {
    this.$refs.map.addInitHook("addHandler", "gestureHandling", GestureHandling)
  },
}
</script>

<template>
  <div class="map-wrapper">
    <LMap
      ref="map"
      :zoom="constants.ZOOM_LEVEL.STREETS"
      :min-zoom="constants.ZOOM_LEVEL.CITY"
      :center="calculateCenterCoords()"
      :options="mapOptions"
    >
      <Vue2LeafletGoogleMutant :apikey="API_KEY"/>
      <LMarker
        v-for="(marker, index) in markers"
        :key="index"
        :lat-lng="getPosition(marker)"
        @click="navigate(marker)"
        @mouseenter="openPopup"
      >
        <LIcon :icon-url="getMarkerIcon()"/>
        <LPopup>
          <div class="popup-container">
            <div
              class="like-btn"
              :class="isLiked(marker.attractionData.id) ? 'like-btn-liked' : 'like-btn'"
              @click.stop="like(marker.attractionData.id)"
            >
            </div>
            <router-link
              :to="{
                name: 'attraction',
                params: {
                  slug: marker.attractionData.slug,
                },
              }"
              class="link"
            >
              <div class="attraction-image" :style="getBackground(marker.attractionData.image)">
                <div></div>
              </div>
              <div class="text-content">
                <h5 class="attraction-title-popup">{{ getTranslatedContent('title', marker.attractionData.content) }}</h5>
                <p class="address-line">{{ marker.attractionData.address }}</p>
              </div>
            </router-link>
          </div>
        </LPopup>
      </LMarker>
      <LControl position="topleft">
        <div class="filters-container">
          <div
            class="filter"
            :class="selectedFilter === 'attractions' && 'active'"
            @click="filter('attractions')"
          >
            {{ getTranslatedContent('ATTRACTIONS_map_all') }}
          </div>
          <div
            class="filter"
            :class="selectedFilter === 'likedAttractions' && 'active'"
            @click="filter('likedAttractions')"
          >
            {{ getTranslatedContent('ATTRACTIONS_map_favorite') }}
          </div>
          <div
            class="filter"
            :class="selectedFilter === 'freeEntryAttractions' && 'active'"
            @click="filter('freeEntryAttractions')"
          >
            {{ getTranslatedContent('ATTRACTIONS_map_free') }}
          </div>
        </div>
      </LControl>
      <LControlFullscreen v-if="!desktopScreen" position="bottomleft"/>
      <LControlZoom position="bottomright"/>
    </LMap>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.filters-container {
  display: flex;
  border-radius: 5px;
  overflow: auto;
}

.filter {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 150px;
  height: 38px;
  margin-right: 3px;
  background: $white;
  color: $dark-blue;
  font-family: Open Sans Semibold, sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
  cursor: pointer;

  &.active {
    background: $dark-blue;
    color: $white;
  }

  &:last-child {
    margin-right: 0;
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.4;
  }
}

.link {
  text-decoration: none;
}

.map-wrapper {
  height: 638px;
}

.google-map {
  width: 100%;
  height: 100%;
}

.google-map-expanded {
  position: absolute;
  width: 100%;
  height: 643px;
  left: 0;
}

.popup-container {
  width: 100%;
  min-height: 203px;
  padding-bottom: 13px;

  & .attraction-image {
    background: $light-gray;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;

    width: 231px;
    height: 136px;

    // border: 1px solid #FFFFFF;
    box-sizing: border-box;
    border-radius: 5px 5px 0 0;
  }

  & .like-btn {
    background: url("../../assets/icons/heart-disable.svg") center no-repeat;
    width: 24px;
    height: 24px;
    margin-left: 195px;
    margin-top: 13px;
    margin-right: 11.55px;
    cursor: pointer;
    position: absolute;
    z-index: 100;

    &:hover {
      background: url("../../assets/icons/heart-active.svg") center no-repeat;
      opacity: 0.8;
    }
  }

  & .like-btn-liked {
    background: url("../../assets/icons/heart-active.svg") center no-repeat;
  }

  & .text-content {
    padding-left: 13px;
    padding-right: 9px;
    padding-top: 11px;

    & h5 {
      font-family: Open Sans Bold, sans-serif;
      font-style: normal;
      font-weight: bold;
      font-size: 14px;
      line-height: 18px;
      text-transform: uppercase;
      color: #252527;
    }

    & .address-line {
      font-family: "Open Sans", sans-serif;
      font-style: normal;
      font-weight: 600;
      font-size: 12px;
      line-height: 16px;
      color: rgba(37, 44, 62, 0.6);
      margin: 7px 0 0;
    }
  }
}

// leaflet css overrides

::v-deep .leaflet-popup-content {
  margin: 0;
  width: 231px !important;
}

::v-deep .leaflet-popup-close-button {
  display: none;
}

::v-deep .leaflet-popup-content-wrapper {
  border-radius: 5px;
  padding: 0;
}

::v-deep .leaflet-popup {
  margin-bottom: 70px !important;
}

::v-deep .vue2leaflet-map {
  z-index: 0;
}

:v-deep .leaflet-marker-icon {
  cursor: pointer !important;
}

::v-deep .leaflet-control-attribution.leaflet-control {
  display: none !important;
}

@media only screen and (max-width: $screen-width-xs + 1) {
  .map-wrapper {
    height: 100%;
  }

  .filters-container {
    flex-direction: column;
    border-radius: 0
  }

  .filter {
    border-radius: 5px;
    margin: 0 0 3px;
  }

  .popup-container {
    & .like-btn-liked {
      &:hover {
        background: url("../../assets/icons/heart-disable.svg") center no-repeat;
      }
    }
  }
}
</style>
